import React, { useState, useEffect } from 'react';
import { Search, Plus, MoreVertical, Edit, Trash2, Play, Pause, RefreshCw, Bot, FileText, Phone, ExternalLink, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useClientSettings } from '../../contexts/ClientSettingsContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { clientAPI } from '../../services/api';
import ScriptEnhancer from '../../components/ScriptEnhancer';

const ClientAgents = () => {
  const { clientUser } = useAuth();
  const { usage, canCreateMore, getLimit, refreshUsage } = useClientSettings();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [actionMenu, setActionMenu] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingAgent, setEditingAgent] = useState(null);
  const [viewingScript, setViewingScript] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    language: 'English',
    voice_type: 'female',
    script: '',
    greeting: 'Hello! How can I help you today?',
    description: ''
  });

  const languages = ['English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Chinese', 'Japanese', 'Korean', 'Arabic', 'Hindi', 'Russian', 'Uzbek'];
  const voiceTypes = ['female', 'male', 'neutral'];

  useEffect(() => {
    fetchAgents();
  }, [clientUser]);

  const fetchAgents = async () => {
    if (!clientUser?.id) return;
    setLoading(true);
    try {
      const response = await clientAPI.getAgents(clientUser?.id);
      setAgents(response.data.agents || []);
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError('Failed to fetch agents');
    } finally {
      setLoading(false);
    }
  };

  const filteredAgents = agents.filter(agent =>
    agent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.language?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.name || !formData.script) {
      setError('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      await clientAPI.createAgent(clientUser?.id, {
        ...formData,
        owner_id: clientUser?.id
      });
      setSuccess('Agent created successfully');
      setShowModal(false);
      setFormData({
        name: '',
        language: 'English',
        voice_type: 'female',
        script: '',
        greeting: 'Hello! How can I help you today?',
        description: ''
      });
      fetchAgents();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error creating agent:', err);
      setError(err.response?.data?.detail || 'Failed to create agent');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAgent = async (e) => {
    e.preventDefault();
    if (!editingAgent) return;
    
    setLoading(true);
    try {
      await clientAPI.updateAgent(clientUser?.id, editingAgent.id, formData);
      setSuccess('Agent updated successfully');
      setShowEditModal(false);
      setEditingAgent(null);
      fetchAgents();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating agent:', err);
      setError(err.response?.data?.detail || 'Failed to update agent');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateScript = async (e) => {
    e.preventDefault();
    if (!viewingScript) return;
    
    setLoading(true);
    try {
      await clientAPI.updateAgentScript(clientUser?.id, viewingScript.id, formData.script);
      setSuccess('Script updated successfully');
      setShowScriptModal(false);
      setViewingScript(null);
      fetchAgents();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating script:', err);
      setError(err.response?.data?.detail || 'Failed to update script');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (agentId, newStatus) => {
    setLoading(true);
    try {
      await clientAPI.updateAgent(clientUser?.id, agentId, { status: newStatus });
      setSuccess(`Agent ${newStatus === 'active' ? 'activated' : 'paused'} successfully`);
      fetchAgents();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update agent status');
    } finally {
      setLoading(false);
      setActionMenu(null);
    }
  };

  const handleDelete = async (agentId) => {
    if (!window.confirm('Are you sure you want to delete this agent?')) return;
    
    setLoading(true);
    try {
      await clientAPI.deleteAgent(clientUser?.id, agentId);
      setSuccess('Agent deleted successfully');
      fetchAgents();
      refreshUsage(); // Refresh usage stats
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting agent:', err);
      setError('Failed to delete agent');
    } finally {
      setLoading(false);
      setActionMenu(null);
    }
  };

  const openEditModal = (agent) => {
    setEditingAgent(agent);
    setFormData({
      name: agent.name,
      language: agent.language,
      voice_type: agent.voice_type,
      script: agent.script,
      greeting: agent.greeting,
      description: agent.description || ''
    });
    setShowEditModal(true);
    setActionMenu(null);
  };

  const openScriptModal = (agent) => {
    setViewingScript(agent);
    setFormData({ ...formData, script: agent.script });
    setShowScriptModal(true);
    setActionMenu(null);
  };

  // Calculate agent quota info
  const agentUsage = usage?.usage?.voice_agents || { used: agents.length, limit: getLimit('max_voice_agents') || 5 };
  const canCreateAgent = canCreateMore('voice_agents');

  return (
    <div className="p-8" data-testid="client-agents-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">{t('voiceAgents.title')}</h1>
          <p className="text-gray-400">{t('voiceAgents.subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchAgents}
            disabled={loading}
            className="flex items-center gap-2 border border-gray-700 text-gray-300 hover:bg-gray-800 px-4 py-2 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            {t('voiceAgents.refresh')}
          </button>
          <button
            onClick={() => {
              if (!canCreateAgent) {
                setError(t('voiceAgents.limitReached'));
                return;
              }
              setFormData({
                name: '',
                language: 'English',
                voice_type: 'female',
                script: '',
                greeting: 'Hello! How can I help you today?',
                description: ''
              });
              navigate('/client/agents/new');
            }}
            disabled={!canCreateAgent}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              canCreateAgent 
                ? 'bg-teal-400 hover:bg-teal-500 text-black' 
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="create-agent-btn"
          >
            <Plus className="w-5 h-5" />
            {t('voiceAgents.createAgent')}
          </button>
        </div>
      </div>

      {/* Agent Limit Banner */}
      <div className={`mb-6 p-4 rounded-xl border ${
        agentUsage.used >= agentUsage.limit 
          ? 'bg-red-500/10 border-red-500/30' 
          : agentUsage.limit - agentUsage.used <= 1 
            ? 'bg-yellow-500/10 border-yellow-500/30'
            : 'bg-blue-500/10 border-blue-500/30'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className={`w-5 h-5 ${
              agentUsage.used >= agentUsage.limit ? 'text-red-400' : 
              agentUsage.limit - agentUsage.used <= 1 ? 'text-yellow-400' : 'text-blue-400'
            }`} />
            <div>
              <p className={`font-medium ${
                agentUsage.used >= agentUsage.limit ? 'text-red-400' : 
                agentUsage.limit - agentUsage.used <= 1 ? 'text-yellow-400' : 'text-blue-400'
              }`}>
                {t('voiceAgents.agentQuota')}: {agentUsage.used} / {agentUsage.limit}
              </p>
              <p className="text-gray-400 text-sm">
                {agentUsage.used >= agentUsage.limit 
                  ? t('voiceAgents.noSlotsRemaining')
                  : `${agentUsage.limit - agentUsage.used} ${t('voiceAgents.slotsRemaining')}`
                }
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="w-32 bg-gray-700 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${
                  agentUsage.used >= agentUsage.limit ? 'bg-red-400' : 
                  agentUsage.limit - agentUsage.used <= 1 ? 'bg-yellow-400' : 'bg-blue-400'
                }`}
                style={{ width: `${Math.min((agentUsage.used / agentUsage.limit) * 100, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
          {error}
          <button onClick={() => setError('')} className="float-right text-red-400 hover:text-red-300">&times;</button>
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400">
          {success}
        </div>
      )}

      {/* Search */}
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              placeholder={t('voiceAgents.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg pl-12 pr-4 py-2.5 text-white placeholder-gray-500 outline-none focus:border-teal-400"
              data-testid="search-agents-input"
            />
          </div>
        </div>
      </div>

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAgents.length === 0 ? (
          <div className="col-span-full bg-[#0d1117] border border-gray-800 rounded-xl p-12 text-center">
            <Bot className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500">
              {loading ? t('voiceAgents.loadingAgents') : t('voiceAgents.noAgentsFound')}
            </p>
          </div>
        ) : (
          filteredAgents.map((agent) => (
            <div key={agent.id} className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 hover:border-gray-700 transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <div className="relative">
                  <button
                    onClick={() => setActionMenu(actionMenu === agent.id ? null : agent.id)}
                    className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                    data-testid={`agent-action-menu-${agent.id}`}
                  >
                    <MoreVertical className="w-5 h-5 text-gray-400" />
                  </button>
                  {actionMenu === agent.id && (
                    <div className="absolute right-0 top-full mt-1 w-48 bg-[#1a1a1f] border border-gray-700 rounded-lg shadow-xl z-10">
                      <button
                        onClick={() => openEditModal(agent)}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-gray-300 hover:bg-gray-800 transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                        {t('voiceAgents.editAgent')}
                      </button>
                      <button
                        onClick={() => openScriptModal(agent)}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-gray-300 hover:bg-gray-800 transition-colors"
                      >
                        <FileText className="w-4 h-4" />
                        {t('voiceAgents.viewScript')}
                      </button>
                      {agent.status === 'active' ? (
                        <button
                          onClick={() => handleStatusChange(agent.id, 'paused')}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-yellow-400 hover:bg-gray-800 transition-colors"
                        >
                          <Pause className="w-4 h-4" />
                          Pause Agent
                        </button>
                      ) : (
                        <button
                          onClick={() => handleStatusChange(agent.id, 'active')}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-green-400 hover:bg-gray-800 transition-colors"
                        >
                          <Play className="w-4 h-4" />
                          Activate Agent
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(agent.id)}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-red-400 hover:bg-gray-800 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        {t('voiceAgents.deleteAgent')}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <h3 className="text-lg font-semibold text-white mb-2">{agent.name}</h3>
              <p className="text-gray-500 text-sm mb-4 line-clamp-2">{agent.description || agent.greeting}</p>

              <div className="flex flex-wrap gap-2 mb-4">
                <span className="px-2 py-1 bg-blue-400/10 text-blue-400 rounded text-xs">{agent.language}</span>
                <span className="px-2 py-1 bg-purple-400/10 text-purple-400 rounded text-xs capitalize">{agent.voice_type}</span>
                <span className={`px-2 py-1 rounded text-xs ${
                  agent.status === 'active' ? 'bg-green-400/10 text-green-400' : 'bg-gray-400/10 text-gray-400'
                }`}>
                  {agent.status}
                </span>
              </div>

              <div className="flex items-center justify-between text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  {agent.total_calls || 0} calls
                </span>
                <button
                  onClick={() => navigate(`/client/agents/${agent.id}`)}
                  className="flex items-center gap-1 text-teal-400 hover:text-teal-300 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Edit
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Agent Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-white mb-6">Create New Voice Agent</h3>
            <form onSubmit={handleCreateAgent} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Agent Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                    placeholder="e.g., Sales Assistant"
                    required
                    data-testid="create-agent-name"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Language</label>
                  <select
                    value={formData.language}
                    onChange={(e) => setFormData({...formData, language: e.target.value})}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400"
                  >
                    {languages.map(lang => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Voice Type</label>
                  <select
                    value={formData.voice_type}
                    onChange={(e) => setFormData({...formData, voice_type: e.target.value})}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400"
                  >
                    {voiceTypes.map(type => (
                      <option key={type} value={type} className="capitalize">{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Greeting</label>
                  <input
                    type="text"
                    value={formData.greeting}
                    onChange={(e) => setFormData({...formData, greeting: e.target.value})}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                    placeholder="Hello! How can I help you today?"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-300 text-sm mb-2">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="Brief description of this agent's purpose"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm mb-2">Agent Script *</label>
                <ScriptEnhancer
                  value={formData.script}
                  onChange={(value) => setFormData({...formData, script: value})}
                  fieldType="system_prompt"
                  placeholder="Enter the conversation script for your AI agent...

Example:
- If customer asks about pricing, respond with: 'Our plans start at $29/month...'
- If customer wants to schedule, ask: 'What day and time works best for you?'
- Always be polite and professional"
                  minHeight="200px"
                  maxLength={4000}
                  language={formData.language}
                  showTemplates={true}
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-700 text-gray-300 py-2.5 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-teal-400 hover:bg-teal-500 disabled:bg-teal-400/50 text-black py-2.5 rounded-lg font-medium transition-colors"
                  data-testid="create-agent-submit"
                >
                  {loading ? 'Creating...' : 'Create Agent'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Agent Modal */}
      {showEditModal && editingAgent && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-white mb-6">Edit Voice Agent</h3>
            <form onSubmit={handleUpdateAgent} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Agent Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Language</label>
                  <select
                    value={formData.language}
                    onChange={(e) => setFormData({...formData, language: e.target.value})}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400"
                  >
                    {languages.map(lang => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Voice Type</label>
                  <select
                    value={formData.voice_type}
                    onChange={(e) => setFormData({...formData, voice_type: e.target.value})}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400"
                  >
                    {voiceTypes.map(type => (
                      <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Greeting</label>
                  <input
                    type="text"
                    value={formData.greeting}
                    onChange={(e) => setFormData({...formData, greeting: e.target.value})}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-300 text-sm mb-2">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm mb-2">Agent Script</label>
                <ScriptEnhancer
                  value={formData.script}
                  onChange={(value) => setFormData({...formData, script: value})}
                  fieldType="system_prompt"
                  placeholder="Enter the conversation script for your AI agent..."
                  minHeight="200px"
                  maxLength={4000}
                  language={formData.language}
                  showTemplates={true}
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingAgent(null);
                  }}
                  className="flex-1 border border-gray-700 text-gray-300 py-2.5 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-teal-400 hover:bg-teal-500 disabled:bg-teal-400/50 text-black py-2.5 rounded-lg font-medium transition-colors"
                >
                  {loading ? 'Updating...' : 'Update Agent'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Script Modal */}
      {showScriptModal && viewingScript && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-white">Edit Agent Script</h3>
                <p className="text-gray-500 text-sm mt-1">{viewingScript.name}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                viewingScript.status === 'active' ? 'bg-green-400/10 text-green-400' : 'bg-gray-400/10 text-gray-400'
              }`}>
                {viewingScript.status}
              </span>
            </div>
            <form onSubmit={handleUpdateScript} className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm mb-2">Conversation Script</label>
                <textarea
                  value={formData.script}
                  onChange={(e) => setFormData({...formData, script: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 text-white outline-none focus:border-teal-400 min-h-[400px] font-mono text-sm"
                  placeholder="Enter the conversation script..."
                  data-testid="edit-script-textarea"
                />
              </div>

              <div className="bg-[#1a1a1f] border border-gray-700 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-300 mb-2">Script Tips:</h4>
                <ul className="text-xs text-gray-500 space-y-1">
                  <li>• Use clear, conversational language</li>
                  <li>• Include fallback responses for unexpected questions</li>
                  <li>• Define key phrases and their responses</li>
                  <li>• Specify the tone (professional, friendly, etc.)</li>
                </ul>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowScriptModal(false);
                    setViewingScript(null);
                  }}
                  className="flex-1 border border-gray-700 text-gray-300 py-2.5 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-teal-400 hover:bg-teal-500 disabled:bg-teal-400/50 text-black py-2.5 rounded-lg font-medium transition-colors"
                  data-testid="save-script-btn"
                >
                  {loading ? 'Saving...' : 'Save Script'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientAgents;
